import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Sentry from "@sentry/browser";
import { RootState } from "@src/reducers/store";
import {
  clearFeatureFlags,
  setFeatureFlags,
} from "@src/reducers/feature-flags-slice";
import { fetchFeatureFlags } from "@src/cloud-connect/feature-toggles/repositories/fetch-feature-flags";

/**
 * Keeps feature flags in sync with the authenticated Redux auth context.
 */
export const useSyncFeatureFlagsWithAuthContext = () => {
  const dispatch = useDispatch();
  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );
  const userId = authContext?.userId;
  const namespaceUuid = authContext?.customerNamespace;
  const email = authContext?.userInfoForSupportTicket?.email;

  useEffect(() => {
    if (!userId || !namespaceUuid || !email) {
      dispatch(clearFeatureFlags());
      return;
    }

    let cancelFetch = false;

    fetchFeatureFlags({
      targetingKey: `user-${userId}`,
      namespaceUuid,
      email,
    })
      .then((flags) => {
        if (cancelFetch) return;
        dispatch(setFeatureFlags(flags));
      })
      .catch((err) => {
        if (cancelFetch) return;
        console.error("Failed to load feature flags:", err);
        Sentry.captureException(err);
        // mark as loaded with empty map so feature consumers can keep fallback behavior
        dispatch(setFeatureFlags({}));
      });

    return () => {
      cancelFetch = true;
    };
  }, [dispatch, userId, namespaceUuid, email]);
};
